import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  onboardingApi,
  type RoleUpdateRequest,
  type OrganizationCreateRequest,
} from '../api';
import { useAuth } from '../../../app/providers';

export const useRoleUpdate = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const mutate = async (roleData: RoleUpdateRequest) => {
    setIsPending(true);
    setError(null);
    try {
      await onboardingApi.updateUserRole(roleData);
      // Refresh user data in AuthProvider to update navbar immediately
      await refreshUser();

      // Navigate based on role
      if (roleData.role === 'OWNER') {
        navigate({ to: '/onboarding/packages' });
      } else if (roleData.role === 'MANAGER') {
        navigate({ to: '/' });
      } else {
        navigate({ to: '/resident/onboarding/houses' });
      }
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate,
    isPending,
    error,
  };
};

export const useOrganizationCreate = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();

  const mutate = async (organizationData: OrganizationCreateRequest) => {
    setIsPending(true);
    setError(null);
    try {
      await onboardingApi.createOrganization(organizationData);
      navigate({ to: '/' });
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate,
    isPending,
    error,
  };
};

export const useOnboardingStatus = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const checkStatus = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const status = await onboardingApi.getOnboardingStatus();
      return status;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    checkStatus,
    isLoading,
    error,
  };
};
